import { clamp } from '@/ts/util/util';

export function* previousCurrentPairIterator<T>(arr: T[]) {
    if (!arr.length || arr.length === 1)
        return;

    for (let i = 1; i < arr.length; i++) {
        yield [arr[i - 1], arr[i]];
    }
}

export class PreviousCurrentPairIterator<T> implements Iterator<[first: T, second: T]> {
    private index: number;

    constructor(private values: T[]) {
        this.index = 1; // 1 start on purpose
    }

    private get isDone(): boolean {
        return this.length < 2 || this.index === this.length;
    }

    private get length(): number {
        return this.values.length;
    }

    next(): IteratorResult<[first: T, second: T], number | undefined> {
        if(this.isDone) {
            return {
                done: true,
                value: this.index
            };
        }
        const value: [T, T] = [this.values[this.index - 1], this.values[this.index]];
        this.index++;
        return {
            done: false,
            value
        }
    }

    public static from<T>(values: T[]): Iterable<[T, T]> {
        return {
            [Symbol.iterator]: () => new this(values)
        };
    }
}

export class CircularIterator<T> implements Iterator<T> {
    private index: number;
    private counter = 0;

    constructor(private values: T[], start?: T | number) {
        if (!start || this.values.length === 0)
            this.index = 0;
        else if (typeof start === 'number' && typeof this.values[0] !== 'number') // T !== number
            this.index = start;
        else {
            this.index = this.values.findIndex((value) => start === value);
            if (this.index === -1)
                this.index = 0;
        }
    }

    private get isDone(): boolean {
        return this.counter === this.length;
    }

    private get length(): number {
        return this.values.length;
    }

    next(): IteratorResult<T, number | undefined> {
        if(this.isDone) {
            return {
                done: true,
                value: this.index
            };
        }
        const value = this.values[this.index % this.length];
        this.index++;
        this.counter++;
        return {
            done: false,
            value
        }
    }

    public static from<T>(values: T[], start?: T | number): Iterable<T> {
        return {
            [Symbol.iterator]: () => new this(values, start)
        };
    }
}


export class EveryNthElementIterator<T> implements Iterator<T> {
    private index: number;

    constructor(private values: T[], private n: number) {
        this.index = 0;
        this.n = this.n < 1 ? 1 : this.n;
    }

    private get isDone(): boolean {
        return this.index >= this.length;
    }

    private get length(): number {
        return this.values.length;
    }

    next(): IteratorResult<T, number | undefined> {
        if(this.isDone) {
            return {
                done: true,
                value: this.index
            };
        }
        const value = this.values[this.index];
        this.index += this.n;
        return {
            done: false,
            value
        }
    }

    public static from<T>(values: T[], n: number): Iterable<T> {
        return {
            [Symbol.iterator]: () => new this(values, n)
        };
    }
}


export class SliceIterator<T> implements Iterator<T[]> {
    private index: number;
    private values: T[];
    private sliceAmount: number;

    constructor(values: T[], sliceAmount: number) {
        this.index = 0;
        this.values = values;
        this.sliceAmount = clamp(1, sliceAmount, values.length);
    }

    private get isDone(): boolean {
        return this.index > this.length - this.sliceAmount;
    }

    private get length(): number {
        return this.values.length;
    }

    next(): IteratorResult<T[], number | undefined> {
        if(this.isDone) {
            return {
                done: true,
                value: this.index
            };
        }
        const value = this.values.slice(this.index, this.index + this.sliceAmount);
        this.index += 1;
        return {
            done: false,
            value
        }
    }

    public static from<T>(values: T[], sliceAmount: number): Iterable<T[]> {
        return {
            [Symbol.iterator]: () => new this(values, sliceAmount)
        };
    }
}